"use client";

import { useState } from "react";

import { useRouter } from "next/navigation";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { Aviso, Boton, Campo } from "@/components/campos";
import { type EntrarInput, entrarSchema } from "@/lib/validation/cuenta";

/** Los errores que puede traer el callback de Google en `?error=`, en idioma de persona. */
const ERRORES_GOOGLE: Record<string, string> = {
  google: "No pudimos entrar con Google. Intenta de nuevo o usa tu correo.",
  estado: "El intento de entrar caducó. Vuelve a tocar el botón de Google.",
  correo: "Google no confirmó ese correo. Entra con tu correo y contraseña.",
};

export function EntrarForm({ errorInicial, volver }: { errorInicial?: string; volver?: string }) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(
    errorInicial ? (ERRORES_GOOGLE[errorInicial] ?? ERRORES_GOOGLE.google) : null,
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EntrarInput>({ resolver: zodResolver(entrarSchema) });

  async function onSubmit(datos: EntrarInput) {
    setError(null);
    const res = await fetch("/api/cuenta/entrar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(datos),
    });
    if (!res.ok) {
      setError(((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? "No pudimos entrar.");
      return;
    }
    // `refresh()` además de `push()`: sin él, el marco ya renderizado en el servidor seguiría
    // mostrándose como si no hubiera sesión.
    //
    // ⚠️ `volver` solo se acepta si es una ruta **de esta app** (empieza con "/" y no con "//"): con una
    // URL completa, cualquiera podría mandar un enlace a /entrar?volver=https://otro-sitio y usar el
    // login de Vecipp como trampolín.
    router.push(volver?.startsWith("/") && !volver.startsWith("//") ? volver : "/");
    router.refresh();
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
      {error ? <Aviso>{error}</Aviso> : null}
      <Campo
        etiqueta="Tu correo"
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        placeholder="tucorreo@ejemplo.com"
        error={errors.email?.message}
        {...register("email")}
      />
      <Campo
        etiqueta="Tu contraseña"
        type="password"
        autoComplete="current-password"
        error={errors.password?.message}
        {...register("password")}
      />
      <Boton type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Entrando…" : "Entrar"}
      </Boton>
    </form>
  );
}
